import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Link,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import dayjs from 'dayjs';
import { Launch } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';

import { useApiGet } from '../../../utils/httpClient';
import { getSPDReciprocity } from '../../../endpoints/chapters';
import { reciprocityTypes } from '../../../utils/variables';

const SPDGroupsReciprocities = ({ id }) => {
  const [search, setSearch] = useState('');
  const [filteredReciprocities, setFilteredReciprocities] = useState([]);
  const [reciprocityType, setReciprocityType] = useState('all');

  const { data: testData, isLoading } = useApiGet(
    ['spd-reciprocities', id],
    getSPDReciprocity,
    {
      enabled: true,
      retry: 1,
    },
    {
      id,
    }
  );

  useEffect(() => {
    if (
      testData &&
      testData.length > 0 &&
      (search.length > 0 || reciprocityType !== 'all')
    ) {
      let filtered = testData.filter((reciprocity) =>
        reciprocity.name.toLowerCase().includes(search.toLowerCase())
      );
      if (reciprocityType !== 'all') {
        filtered = filtered.filter(
          (reciprocity) => reciprocity.reciprocity_type_id === reciprocityType
        );
      }
      setFilteredReciprocities(filtered);
    } else {
      if (testData && testData.length > 0) {
        setFilteredReciprocities(testData);
      } else {
        setFilteredReciprocities([]);
      }
    }
  }, [testData, search, reciprocityType]);

  if (isLoading) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">Loading...</Typography>
      </Grid>
    );
  }
  if (!filteredReciprocities) {
    return (
      <Grid item xs={12} textAlign={'center'} paddingTop={2}>
        <Typography variant="body1">No data available</Typography>
      </Grid>
    );
  }
  return (
    <Grid container>
      {!filteredReciprocities ||
        (filteredReciprocities?.length === 0 && (
          <Grid item xs={12} textAlign={'center'} paddingTop={2}>
            <Typography variant="body1">
              No groups or reciprocities assigned
            </Typography>
          </Grid>
        ))}

      {testData?.length > 2 && (
        <>
          <Grid item xs={8} padding>
            <TextField
              label="Search"
              id="search-field"
              size="small"
              value={search}
              fullWidth={true}
              onChange={(event) => setSearch(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={4} padding>
            <FormControl size={'small'} fullWidth={true}>
              <InputLabel id="reciprocity-type-label">Type</InputLabel>
              <Select
                labelId="reciprocity-type-label"
                id="reciprocity-type-select"
                value={reciprocityType}
                onChange={(event) => setReciprocityType(event.target.value)}
                sx={{ textAlign: 'left' }}
                label="Type"
              >
                <MenuItem value={'all'}>All</MenuItem>
                <MenuItem value={reciprocityTypes.Reciprocity}>
                  Reciprocity
                </MenuItem>
                <MenuItem value={reciprocityTypes.Group}>Group</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </>
      )}
      {filteredReciprocities?.map((reciprocity) => (
        <Grid item xs={12} md={6} padding key={reciprocity.id}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={reciprocity.name}
              subheader={
                reciprocity.reciprocity_type_id === reciprocityTypes.Reciprocity
                  ? 'Reciprocity'
                  : 'Group'
              }
              sx={{ p: 1 }}
            />
            <CardContent sx={{ p: 1 }}>
              <Grid container spacing={2}>
                {reciprocity.members
                  .sort((a, b) => a.order - b.order)
                  .map((member) => (
                    <Grid container item xs={12} key={member.id}>
                      <Grid item xs={12}>
                        <Typography variant="body1" fontWeight={'bold'}>
                          {member.role}
                        </Typography>
                        <Typography variant="body1">{member.name}</Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          fontSize={12}
                          paddingBottom={1}
                        >
                          {dayjs(member.start_date).format('MM/DD/YYYY')} -
                        </Typography>
                        <Typography
                          variant="caption"
                          fontSize={12}
                          paddingBottom={1}
                          paddingLeft={1}
                        >
                          {member.end_date
                            ? dayjs(member.end_date).format('MM/DD/YYYY')
                            : 'Present'}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
              </Grid>
            </CardContent>
            <CardActions>
              <Grid item xs={12} textAlign={'right'}>
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`/reciprocity/${reciprocity.id}`}
                  underline="hover"
                  color="primary"
                >
                  View Details
                  <Launch sx={{ marginLeft: 1, fontSize: 16 }} />
                </Link>
              </Grid>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default SPDGroupsReciprocities;
